import { Injectable } from '@angular/core';
import { firstValueFrom, of } from 'rxjs';
import { catchError, timeout } from 'rxjs/operators';
import { ItemsService } from 'src/app/pages/items/services/items.service';
import { LoggerService } from 'src/app/services/logger.service';
import { BrowserStorageService } from 'src/app/services/browser-storage.service';
import { environment } from 'src/environments/environment';
import { FieldTypeRow,
  formulaFieldTypeId,
  isFormulaType,
  QuoteFormulaSummary,
  isLockedQuoteStatus,
  isReservedLineKey,
  formatFormulaDisplayNumber,
  clampDiscountPercent,
  compoundDiscountPercent,
  derivedQuoteImpactFromFormula,
  itemFormulaJsonFromDatas,
  normalizeQuoteImpact,
  parseFormulaJson,
  quoteImpactFromField,
  rollupModeFromField,
} from './formula.types';
import { FormulaContextService } from './formula-context.service';

/**
 * userEdit: the user is editing THIS line right now (line dialog, +/-, a value override, adding
 * it). On a SENT quote that is the only time a calculation may change the line's money; opening or
 * reopening the quote never does.
 */
export type QuoteImpactContext = { quoteStatus?: string; isEditable?: boolean; userEdit?: boolean };

@Injectable({ providedIn: 'root' })
export class FormulaRuntimeService {
  private formulaFieldsCache: any[] | null = null;
  private loadPromise: Promise<any[]> | null = null;
  private loadGeneration = 0;
  private fieldsLoaded = false;
  private fieldSumsCache: QuoteFormulaSummary[] | null = null;
  private fieldSumsCacheKey = '';
  /** Workspace the cached fields belong to. Logout / sign-in / switch change it without a reload. */
  private cacheMerchantId: string | null = null;
  /** Names of the workspace's custom fields (from customFieldList): the flat-key allowlist. */
  private customFieldNames: ReadonlySet<string> = new Set();
  /** Every custom field of the workspace by id (name, key, type, unit), for rows that arrive nameless. */
  private fieldDefsById = new Map<string, any>();

  constructor(
    private readonly contextService: FormulaContextService,
    private readonly itemsService: ItemsService,
    private readonly logger: LoggerService,
    private readonly storage: BrowserStorageService,
  ) {}

  setFormulaFields(fields: any[]): void {
    this.cacheMerchantId = this.currentMerchantId();
    // Every custom field's name (all types): the only flat keys a formula context may read.
    this.customFieldNames = new Set(
      (fields || []).map((f: any) => String(f?.name || '').trim()).filter((n: string) => !!n),
    );
    this.fieldDefsById = new Map(
      (fields || [])
        .filter((f: any) => f?.id != null && String(f?.name || '').trim())
        .map((f: any) => [String(f.id), {
          id: f.id,
          name: f.name,
          field_key: f.field_key,
          field_type_id: f.field_type_id,
          unit_of_measure: f.unit_of_measure,
        }] as [string, any]),
    );
    // Copies, never the response objects: another screen may hold the same array.
    const cache: any[] = [];
    for (const f of fields || []) {
      if (!isFormulaType(f?.field_type_id) || !f?.formula_json) {
        continue;
      }
      let formula = f.formula_json;
      if (typeof formula === 'string') {
        try {
          formula = JSON.parse(formula);
        } catch {
          continue;
        }
      }
      if (formula && typeof formula === 'object') {
        cache.push({ ...f, formula_json: formula });
      }
    }
    this.formulaFieldsCache = cache;
  }

  getFormulaFields(): any[] {
    this.dropCacheIfWorkspaceChanged();
    return this.formulaFieldsCache || [];
  }

  /** The workspace's custom-field names: the flat-key allowlist every formula context uses. */
  getCustomFieldNames(): ReadonlySet<string> {
    return this.customFieldNames;
  }

  /** The last customFieldList attempt failed or timed out: "unknown", NOT "no formula fields". */
  lastLoadFailed(): boolean {
    return this.loadFailedAt > 0;
  }

  /** The field list has been loaded successfully for this workspace. */
  isFieldListLoaded(): boolean {
    this.dropCacheIfWorkspaceChanged();
    return this.fieldsLoaded && this.formulaFieldsCache !== null;
  }

  /**
   * Re-read the workspace field list (units, names, recipes) that another tab, another user or the
   * API may have changed since this session loaded it. The current list stays in use until the new
   * one arrives (no window where the definitions are empty), and a failed read keeps it. True when
   * the fresh list loaded.
   */
  refreshFieldDefinitions(): Promise<boolean> {
    if (!this.hasSession()) {
      return Promise.resolve(false);
    }
    // One refresh at a time: a second quote open while one is in flight shares it.
    if (this.definitionsRefresh) {
      return this.definitionsRefresh;
    }
    const run = this.ensureLoaded(true)
      .then(() => this.isFieldListLoaded() && !this.lastLoadFailed())
      .catch(() => false)
      .finally(() => {
        if (this.definitionsRefresh === run) {
          this.definitionsRefresh = null;
        }
      });
    this.definitionsRefresh = run;
    return run;
  }

  private definitionsRefresh: Promise<boolean> | null = null;

  /** One immediate retry that skips the failure backoff (Save / Send / PDF). True when it loaded. */
  async retryLoadNow(): Promise<boolean> {
    this.loadFailedAt = 0;
    await this.ensureLoaded();
    return this.isFieldListLoaded() && !this.lastLoadFailed();
  }

  /** True once the workspace's fields are loaded and none of them is a formula. */
  hasLoadedNoFormulaFields(): boolean {
    this.dropCacheIfWorkspaceChanged();
    return this.fieldsLoaded && (this.formulaFieldsCache?.length ?? 0) === 0;
  }

  /**
   * Clear session-scoped definitions so the next ensureLoaded() fetches fresh formulas.
   * Call this after formula edits and whenever the authenticated merchant changes.
   */
  invalidateCache(): void {
    this.loadGeneration += 1;
    this.formulaFieldsCache = null;
    // Another workspace's field names must never name this one's rows.
    this.fieldDefsById = new Map();
    // A quote opened after this must not join a refresh started for the previous workspace.
    this.definitionsRefresh = null;
    this.cacheMerchantId = null;
    this.loadFailedAt = 0;
    this.invalidateFieldSumsCache();
    this.fieldsLoaded = false;
    const settle = this.activeLoadSettle;
    if (settle) {
      settle([]);
    }
    this.activeLoadSettle = null;
    this.loadPromise = null;
  }

  private activeLoadSettle: ((rows: any[]) => void) | null = null;

  private currentMerchantId(): string {
    return this.storage.getString('merchant_id', '') || '';
  }

  /**
   * A second workspace must never evaluate with the first one's formula fields. Logout and a
   * sign-in to another workspace stay inside the SPA (this root service survives), so the cache is
   * tied to the merchant_id it was loaded for and dropped as soon as that changes.
   */
  private dropCacheIfWorkspaceChanged(): void {
    const hasState = this.fieldsLoaded || this.formulaFieldsCache !== null || this.loadPromise !== null;
    if (hasState && this.cacheMerchantId !== this.currentMerchantId()) {
      this.invalidateCache();
    }
  }

  /** Same test LoggedGuard uses (userId or loginUserValue present). */
  hasSession(): boolean {
    return !!(this.storage.getString('userId') || this.storage.getString('loginUserValue'));
  }

  /** merchant_id of the signed-in workspace ('' when none). */
  signedInMerchantId(): string {
    return this.currentMerchantId();
  }

  /** customFieldList gives up after this long: Save / Send / PDF await recalcs that await this load. */
  static readonly LOAD_TIMEOUT_MS = 8000;
  /** After a failed load, callers get "no formulas" for this long instead of one request per click. */
  static readonly LOAD_FAILURE_BACKOFF_MS = 30000;
  private loadFailedAt = 0;

  /** At most this many extra attempts when invalidateCache() lands during a load. */
  private static readonly MAX_LOAD_RETRIES = 2;

  async ensureLoaded(force = false, retriesLeft = FormulaRuntimeService.MAX_LOAD_RETRIES): Promise<any[]> {
    // customFieldList is a logged-in endpoint: with no session (public eQuote / invoice pages)
    // there is nothing to load. Not cached, so a later signed-in call still loads.
    if (!this.hasSession()) {
      this.dropCacheIfWorkspaceChanged();
      return [];
    }
    this.dropCacheIfWorkspaceChanged();
    if (!force && this.fieldsLoaded && this.formulaFieldsCache !== null) {
      return this.formulaFieldsCache;
    }
    if (!force && this.loadFailedAt && Date.now() - this.loadFailedAt < FormulaRuntimeService.LOAD_FAILURE_BACKOFF_MS) {
      return this.formulaFieldsCache || []; // degraded backend: map without formulas, no new request
    }
    // Share the in-flight request. Do not bump loadGeneration here.
    // If invalidateCache() settles that request with [], retry so callers
    // are not stuck with the empty placeholder.
    const generation = this.loadGeneration;
    const rows = await this.startFormulaLoad();
    if (generation !== this.loadGeneration) {
      // Bounded: repeated invalidations during a load must not recurse forever.
      if (retriesLeft <= 0) {
        return this.formulaFieldsCache || [];
      }
      return this.ensureLoaded(force, retriesLeft - 1);
    }
    if (this.fieldsLoaded && this.formulaFieldsCache !== null) {
      return this.formulaFieldsCache;
    }
    return rows;
  }

  private startFormulaLoad(): Promise<any[]> {
    if (this.loadPromise) {
      return this.loadPromise;
    }

    const generation = this.loadGeneration;
    const loadMerchantId = this.currentMerchantId();
    this.cacheMerchantId = loadMerchantId;
    let settle: (rows: any[]) => void = () => undefined;
    const pending = new Promise<any[]>((resolve) => {
      settle = resolve;
    });
    this.loadPromise = pending;
    let settled = false;
    const finish = (rows: any[]) => {
      if (settled) {
        return;
      }
      settled = true;
      if (this.activeLoadSettle === finish) {
        this.activeLoadSettle = null;
      }
      if (this.loadPromise === pending) {
        this.loadPromise = null;
      }
      settle(rows);
    };
    this.activeLoadSettle = finish;

    firstValueFrom(
      this.itemsService.itemcustomFieldList({ hideLoader: true }).pipe(
        timeout(FormulaRuntimeService.LOAD_TIMEOUT_MS),
        catchError((err) => {
          this.logger.log('formula:customFieldList failed', err?.message || 'request failed');
          return of(null);
        }),
      ),
    ).then((res: any) => {
      if (generation !== this.loadGeneration) {
        finish([]);
        return;
      }
      if (!res) {
        // Failed or timed-out request: callers map lines WITHOUT formulas. Not cached as "no
        // formula fields"; retried after the backoff.
        this.loadFailedAt = Date.now();
        finish(this.formulaFieldsCache || []);
        return;
      }
      this.loadFailedAt = 0;
      this.setFormulaFields(res?.data || []);
      // Stamp the workspace the request was made for, not whoever is signed in when it lands.
      this.cacheMerchantId = loadMerchantId;
      this.fieldsLoaded = true;
      finish(this.formulaFieldsCache || []);
    }).catch((err) => {
      this.logger.log('formula:ensureLoaded error', err?.message || 'load failed');
      this.loadFailedAt = Date.now();
      finish(this.formulaFieldsCache || []);
    });

    return pending;
  }

  /**
   * The custom-field columns of a quote line, exactly as develop's mappers built them: for each
   * custom-field row with a name and a value, `result[name] = value` (dates through `formatDate`).
   * Empty / 0 values stay hidden as on develop, except a formula's 0, which is a real result.
   * Nothing else from the catalog item is copied: the PR's "flat attribute" loop put every primitive
   * catalog field (cost, fix_cost_per_unit, internal ids, timestamps) on lines that are saved in
   * quote JSON and sent to the public eQuote.
   */
  applyCustomFieldColumns(result: any, datas: any[] | null | undefined, formatDate: (value: any) => any): void {
    if (!result || !Array.isArray(datas)) {
      return;
    }
    for (const customField of datas) {
      const name = customField?.itemsCustomField?.name;
      if (!name) {
        continue;
      }
      const typeId = String(customField?.field_type_id ?? customField?.itemsCustomField?.field_type_id);
      const value = customField.value;
      const hasValue =
        isFormulaType(typeId) ? value !== undefined && value !== null && value !== '' : !!value;
      if (hasValue && !isReservedLineKey(name)) {
        result[name] = typeId === '3' ? formatDate(value) : value;
      }
    }
  }

  /**
   * Custom-field rows with their field's name / key / type filled in from the workspace field list
   * when the row arrived without them. GET /item/:id (merch-backend item.controller view()) returns
   * items_custom_field_datas WITHOUT the nested `itemsCustomField`, so the line dialog, which rebuilds
   * the line from that response, could not name "Weight" and every calculation reading it failed with
   * "Missing value: Weight" on a product that has one (Rod's QA, 2026-10-09). Never copies a formula
   * definition's recipe: a per-product row without its own recipe must stay recipe-less.
   */
  withFieldDefinitions(rows: any[] | null | undefined): any[] {
    if (!Array.isArray(rows)) {
      return [];
    }
    return rows.map((row: any) => {
      if (!row || typeof row !== 'object' || row?.itemsCustomField?.name) {
        return row;
      }
      const def = this.fieldDefsById.get(String(row.customerCustomFieldId ?? row.custom_field_id ?? row?.itemsCustomField?.id ?? ''));
      if (!def) {
        return row;
      }
      return {
        ...row,
        field_type_id: row.field_type_id ?? def.field_type_id,
        itemsCustomField: { ...(row.itemsCustomField || {}), ...def },
      };
    });
  }

  /**
   * The catalog's custom-field rows (`primary`, the product as it is now) with gaps filled from the
   * line's own rows of the same field (`fallback`), for the line dialog's rebuild:
   *   - the field's name / key / type, and any key the catalog row does not carry at all
   *     (undefined). A null is the product's answer, never a gap: a value cleared on the product
   *     stays cleared, and formula_json null (no own recipe) never takes the line's recipe;
   *   - never a row the product no longer has (removed fields are not resurrected from the line);
   *   - no catalog list at all (the response carried no custom-field rows): the line's rows.
   * GET /item/:id returns rows without the nested `itemsCustomField`, so rebuilding from it alone
   * lost every name (Weight read as "Missing value") and the line's own state.
   */
  static mergeCustomFieldRows(primary: any[] | null | undefined, fallback: any[] | null | undefined): any[] {
    if (!Array.isArray(primary)) {
      return (Array.isArray(fallback) ? fallback : []).filter(Boolean).map((row: any) => ({ ...row }));
    }
    const fallbackByKey = new Map<string, any>();
    for (const row of Array.isArray(fallback) ? fallback : []) {
      const k = FormulaRuntimeService.fieldRowKey(row);
      if (row && k && !fallbackByKey.has(k)) {
        fallbackByKey.set(k, row);
      }
    }
    return primary.filter(Boolean).map((row: any) => {
      const other = fallbackByKey.get(FormulaRuntimeService.fieldRowKey(row));
      if (!other) {
        return { ...row };
      }
      const out: any = { ...row };
      for (const [k, v] of Object.entries(other)) {
        if (k === 'itemsCustomField') continue;
        if (out[k] === undefined) {
          out[k] = v;
        }
      }
      if (row.itemsCustomField || other.itemsCustomField) {
        const { formula_json: _recipe, ...otherField } = other.itemsCustomField || {};
        out.itemsCustomField = FormulaRuntimeService.fillMissing(row.itemsCustomField || {}, otherField);
      }
      return out;
    });
  }

  /** The field a custom-field row belongs to (the same id order the formula lookups use). */
  static fieldRowKey(row: any): string {
    return String(row?.customerCustomFieldId ?? row?.custom_field_id ?? row?.itemsCustomField?.id ?? '');
  }

  /**
   * The REGULAR discount on a line (the one picked in the line dialog), as a percent, separate from
   * what a discount calculation adds on top. Sources, in order: `regularDiscount` (written whenever
   * a calculation discount is applied); a line a calculation wrote before that key existed (its
   * applyDiscount is the calculation's own output, so the regular part is 0 unless the user has
   * changed it since); otherwise applyDiscount.
   */
  /**
   * Two discount percents are the same when they are equal at 2 decimals, the precision every
   * discount is written and shown at (compoundDiscountPercent rounds to it). A float tolerance could
   * fire at exactly a rounding boundary (41.375 vs "41.38"), and a drift read as a user edit
   * would compound again.
   */
  static sameDiscountPercent(a: number, b: number): boolean {
    return Math.round(Number(a) * 100) === Math.round(Number(b) * 100);
  }

  static regularDiscountOf(line: any): number {
    const parse = (v: any) =>
      v === undefined || v === null || v === '' || v === 0 || v === false
        ? 0
        : clampDiscountPercent(FormulaContextService.parseDiscount(v));
    const current = parse(line?.applyDiscount);
    const applied = line?.formulaImpactApplied;
    if (applied && applied.discount !== undefined && applied.discount !== null) {
      const written = Number(applied.effectiveDiscount ?? applied.discount);
      if (Number.isFinite(written) && !FormulaRuntimeService.sameDiscountPercent(current, written)) {
        // applyDiscount is no longer what the calculation wrote: the user set it since. That beats a
        // stored regularDiscount, which may be carried over from before the change.
        return current;
      }
      const stored = line?.regularDiscount;
      // Unchanged since the calculation wrote it: the stored regular part (0 for a line written
      // before regularDiscount existed, whose applyDiscount is the calculation's own output).
      return stored !== undefined && stored !== null && stored !== '' ? parse(stored) : 0;
    }
    return current;
  }

  /**
   * What the line dialog's discount picker should show: the regular part on a line a calculation
   * discounts (never the effective, compounded figure), the line's applyDiscount otherwise.
   */
  static regularDiscountForDialog(line: any): any {
    const applied = line?.formulaImpactApplied;
    if (applied && applied.discount !== undefined && applied.discount !== null) {
      const regular = FormulaRuntimeService.regularDiscountOf(line);
      return regular > 0 ? regular : undefined;
    }
    return line?.applyDiscount;
  }

  /** Price rows reduced to what quotes show and formulas read: the price JSON and the discount. */
  static publicPriceRows(prices: any): any {
    if (!Array.isArray(prices)) {
      return undefined;
    }
    return prices.map((p: any) => ({ price: p?.price, discount: p?.discount }));
  }

  /**
   * The catalog-item inputs a formula recalc reads (custom-field rows with their per-item recipe,
   * price tiers + discount, unit quantity, and the item's own custom-field values when an API
   * flattened them onto the item by name). Cost, commission, min price and internal fields are
   * left out on purpose: this object is saved on the quote line.
   */
  static formulaSourceOf(item: any): any {
    if (!item || typeof item !== 'object') {
      return null;
    }
    const datas = Array.isArray(item.items_custom_field_datas)
      ? item.items_custom_field_datas
      : Array.isArray(item.itemsCustomFields) ? item.itemsCustomFields : [];
    const pick = (cf: any) => cf ? {
      id: cf.id,
      name: cf.name,
      field_key: cf.field_key,
      field_type_id: cf.field_type_id,
      unit_of_measure: cf.unit_of_measure,
      execution_order: cf.execution_order,
      formula_json: cf.formula_json,
    } : cf;
    const source: any = {
      id: item.id,
      unit_quantity: item.unit_quantity,
      item_prices: FormulaRuntimeService.publicPriceRows(item.item_prices),
      items_custom_field_datas: datas.map((d: any) => ({
        customerCustomFieldId: d?.customerCustomFieldId,
        custom_field_id: d?.custom_field_id,
        itemId: d?.itemId ?? d?.item_id,
        field_type_id: d?.field_type_id,
        value: d?.value,
        apply_formula: d?.apply_formula,
        formula_json: d?.formula_json,
        item_formula_json: d?.item_formula_json,
        itemsCustomField: pick(d?.itemsCustomField),
      })),
    };
    for (const d of datas) {
      const name = d?.itemsCustomField?.name;
      const v = name ? item[name] : undefined;
      if (name && v !== undefined && v !== null && typeof v !== 'object') {
        source[name] = v;
      }
    }
    return source;
  }

  /** Deep-clone a quote line so recalculate/apply never mutate the live table row. */
  cloneQuoteLine(row: any): any {
    if (!row) {
      return row;
    }
    return {
      ...row,
      item_prices: this.clonePriceRows(row.item_prices),
      itemsCustomFields: Array.isArray(row.itemsCustomFields)
        ? row.itemsCustomFields.map((cf: any) => ({
            ...cf,
            itemsCustomField: cf?.itemsCustomField ? { ...cf.itemsCustomField } : cf?.itemsCustomField,
          }))
        : row.itemsCustomFields,
    };
  }

  /**
   * Build quote rows synchronously. applyToLine returns a line object.
   * A Promise is never inserted into the table.
   */
  syncMapLines(items: any[], mapLine: (item: any) => any): any[] {
    const rows: any[] = [];
    const list = Array.isArray(items) ? items : [];
    for (const item of list) {
      const mapped = mapLine(item);
      const mappedRows = Array.isArray(mapped) ? mapped : [mapped];
      for (const row of mappedRows) {
        if (row && typeof row.then === 'function') {
          this.logger.log('formula:syncMapLines skipped Promise', 'applyToLine must stay synchronous');
          continue;
        }
        rows.push(row);
      }
    }
    return rows;
  }

  /** Apply formula results onto a quote line, including optional Price / Discount impact. */
  applyToLine(
    result: any,
    sourceItem: any,
    quantity?: number,
    formulaFields?: any[],
    impact?: QuoteImpactContext | null,
  ): any {
    const fields = formulaFields || this.getFormulaFields();
    if (!fields.length || !result) {
      return result;
    }

    const next = this.cloneQuoteLine(result);
    if (impact?.quoteStatus) {
      next.quote_status = impact.quoteStatus;
    }
    if (impact?.isEditable === false) {
      next.isEditable = false;
    }
    if (impact?.isEditable === true) {
      next.isEditable = true;
    }

    const resolvedQuantity =
      quantity ??
      next?.Quantity ??
      next?.QTY ??
      next?.QUANTITY ??
      next?.quantity ??
      sourceItem?.quantity ??
      sourceItem?.unit_quantity;
    // Formulas read the line's BASE (pre-impact) unit price and discount, never what a previous
    // run wrote: "50% of Unit Price" applied to its own output halved the price on every add,
    // +/- and quote open ($40 -> $10 -> $5 -> $2.50 on QA4).
    const base = this.resolveFormulaBase(next, sourceItem);
    next.formulaBase = base;
    const lineItem = {
      ...sourceItem,
      ...next,
      ...FormulaRuntimeService.baseMoneyKeys(base),
      quantity: resolvedQuantity,
      Quantity: resolvedQuantity,
      QTY: next?.QTY ?? resolvedQuantity,
      itemsComing: next?.itemsComing || sourceItem?.itemsComing,
      items_custom_field_datas: this.withFieldDefinitions(
        sourceItem?.items_custom_field_datas ||
        next?.itemsCustomFields ||
        sourceItem?.itemsCustomFields ||
        [],
      ),
      item_prices: this.clonePriceRows(sourceItem?.item_prices || next?.item_prices),
    };

    const existing = this.withFieldDefinitions(Array.isArray(next.itemsCustomFields) ? next.itemsCustomFields : []);
    const existingByFieldId = new Map<string, any>();
    for (const cf of existing) {
      const id = String(cf?.itemsCustomField?.id ?? cf?.customerCustomFieldId ?? '');
      const name = String(cf?.itemsCustomField?.name ?? '');
      if (id) existingByFieldId.set(`id:${id}`, cf);
      if (name) existingByFieldId.set(`name:${name}`, cf);
    }

    const datas = this.withFieldDefinitions(this.collectCustomFieldRows(lineItem, next));
    const applyFlags = this.collectApplyFlags(datas);
    // The values a formula reads: the catalog rows as before, gaps filled from the line's rows (a
    // rebuilt line whose catalog copy lacks a value must not read "Missing value").
    lineItem.items_custom_field_datas = this.withFieldDefinitions(
      FormulaRuntimeService.mergeCustomFieldRows(lineItem.items_custom_field_datas, datas),
    );

    // A loop, not flatMap: the jest tsconfig's lib is es2018 and the spec would not compile.
    const activeFields: any[] = [];
    for (const f of fields) {
      const itemFormula = itemFormulaJsonFromDatas(datas, f.id);
      // No per-item formula → skip. Explicit false (Apply on quote off) → skip.
      if (!itemFormula || applyFlags.get(String(f.id)) === false) {
        continue;
      }
      activeFields.push({ ...f, formula_json: itemFormula });
    }

    // An item with no Formula field at all: return the line exactly as given. The bookkeeping below
    // (formulaBase, formulaSource, formulaResults, public price rows, quote_status/isEditable) is
    // formula-only; writing it on every line added ~3 KB to each saved non-formula quote.
    const hasFormulaRow = datas.some(
      (d: FieldTypeRow) => isFormulaType(d?.field_type_id ?? d?.itemsCustomField?.field_type_id),
    );
    const hadResults = !!result.formulaResults && Object.keys(result.formulaResults).length > 0;
    if (!activeFields.length && !hasFormulaRow && !hadResults) {
      return result;
    }

    const evaluated = this.contextService.evaluateFormulaFields(
      lineItem,
      activeFields,
      lineItem.quantity,
      this.customFieldNames,
    );

    const formulaConfigById = new Map<string, any>();
    for (const d of datas) {
      const fieldId = String(
        d.customerCustomFieldId || d.custom_field_id || d.itemsCustomField?.id || '',
      );
      const fieldDef = fields.find((f) => String(f.id) === fieldId);
      if (!fieldId || !fieldDef) {
        continue;
      }
      const itemFormula = itemFormulaJsonFromDatas(datas, fieldId);
      if (!itemFormula) {
        continue;
      }
      formulaConfigById.set(fieldId, {
        value: d.value ?? '',
        apply_formula:
          d.apply_formula === undefined || d.apply_formula === null
            ? true
            : !!d.apply_formula,
        field_type_id: formulaFieldTypeId(),
        customerCustomFieldId: fieldDef.id,
        formula_json: itemFormula,
        itemsCustomField: {
          id: fieldDef.id,
          name: fieldDef.name,
          field_key: fieldDef.field_key,
          unit_of_measure: fieldDef.unit_of_measure,
          formula_json: itemFormula,
          field_type_id: formulaFieldTypeId(),
          execution_order: fieldDef.execution_order,
        },
      });
    }

    const formulaEntries = Object.values(evaluated).map((entry: any) => {
      const field = entry.field;
      const fieldId = String(field.id);
      const prev =
        existingByFieldId.get(`id:${field.id}`) ||
        existingByFieldId.get(`name:${field.name}`);
      const unit = field.unit_of_measure || '';
      const calculatedDisplay = entry.error
        ? 'Error'
        : `${formatFormulaDisplayNumber(entry.value)}${unit ? ' ' + unit : ''}`;

      const itemFormula = parseFormulaJson(field.formula_json) || field.formula_json;
      const baseConfig = formulaConfigById.get(fieldId) || {
        apply_formula: applyFlags.get(fieldId) !== false,
        field_type_id: formulaFieldTypeId(),
        customerCustomFieldId: field.id,
        formula_json: itemFormula,
        itemsCustomField: {
          id: field.id,
          name: field.name,
          field_key: field.field_key,
          unit_of_measure: field.unit_of_measure,
          formula_json: itemFormula,
          field_type_id: formulaFieldTypeId(),
          execution_order: field.execution_order,
        },
      };

      // Keep a user override on the quote line across qty recalculations.
      if (prev?.isOverridden) {
        return {
          ...prev,
          ...baseConfig,
          field_type_id: formulaFieldTypeId(),
          customerCustomFieldId: field.id,
          calculatedValue: entry.error ? null : String(entry.value),
          calculatedDisplayValue: calculatedDisplay,
          quote_impact: entry.quote_impact ?? prev.quote_impact,
          itemsCustomField: baseConfig.itemsCustomField,
        };
      }

      return {
        ...baseConfig,
        value: entry.error ? null : String(entry.value),
        displayValue: calculatedDisplay,
        calculatedValue: entry.error ? null : String(entry.value),
        calculatedDisplayValue: calculatedDisplay,
        isOverridden: false,
        quote_impact: entry.quote_impact,
        field_type_id: formulaFieldTypeId(),
        customerCustomFieldId: field.id,
      };
    });

    const withoutFormulas = existing.filter(
      (cf: any) =>
        !isFormulaType(cf?.field_type_id) &&
        !isFormulaType(cf?.itemsCustomField?.field_type_id),
    );

    const inactiveFormulaConfigs = Array.from(formulaConfigById.values()).filter(
      (cfg) => !evaluated[String(cfg.customerCustomFieldId)] &&
        !Object.values(evaluated).some((e: any) => String(e?.field?.id) === String(cfg.customerCustomFieldId)),
    );

    next.itemsCustomFields = [...withoutFormulas, ...inactiveFormulaConfigs, ...formulaEntries];

    formulaEntries.forEach((entry: any) => {
      const name = entry?.itemsCustomField?.name;
      if (name && !isReservedLineKey(name)) {
        next[name] = entry.displayValue ?? entry.value;
      }
    });

    for (const cfg of inactiveFormulaConfigs) {
      const name = cfg?.itemsCustomField?.name;
      if (name && !isReservedLineKey(name)) {
        delete next[name];
      }
    }

    next.formulaResults = evaluated;
    // What a later recalc needs from the catalog item, and nothing else. The PR stored the raw
    // catalog item (fix_cost_per_unit, sales_commission, min_price, internal ids) as `fulldata` and
    // its raw price rows as `item_prices`; both ended up in saved quote JSON and the public eQuote.
    if (sourceItem && sourceItem !== next && sourceItem !== next.formulaSource) {
      next.formulaSource = FormulaRuntimeService.formulaSourceOf(sourceItem);
    }
    if (!next.item_prices && sourceItem?.item_prices) {
      next.item_prices = FormulaRuntimeService.publicPriceRows(sourceItem.item_prices);
    }
    return this.applyQuoteImpact(next, formulaEntries, impact, datas);
  }

  async applyToLineAsync(
    result: any,
    sourceItem: any,
    quantity?: number,
    impact?: QuoteImpactContext | null,
  ): Promise<any> {
    await this.ensureLoaded();
    return this.applyToLine(result, sourceItem, quantity, undefined, impact);
  }

  /**
   * The values a formula reads as Item.Unit_Price / Item.Discount_Percent for this line.
   *   1. The line's snapshot (`formulaBase`), unless the user changed the price / discount since the
   *      formula last wrote it — then what the user set is the new base.
   *   2. No snapshot, line never evaluated (fresh from the catalog): its current values.
   *   3. No snapshot but already evaluated (a line saved before snapshots existed): the catalog
   *      price/discount from the line's formula source, never the possibly-impacted current price.
   * Stored on the line (formulaBase) so it survives save and reload.
   */
  private resolveFormulaBase(line: any, sourceItem: any): { unitPrice: number; discount: number } {
    const num = (v: any) => FormulaContextService.parseNumeric(v);
    const currentPrice = num(line?.perItemPrice ?? line?.newAddedPrice ?? line?.Price);
    const currentDiscount = FormulaContextService.parseDiscount(line?.discount ?? line?.Discount);
    const snapshot = line?.formulaBase;
    if (snapshot && Number.isFinite(Number(snapshot.unitPrice))) {
      const applied = line?.formulaImpactApplied || {};
      const expectedPrice = applied.unitPrice ?? Number(snapshot.unitPrice);
      // A calculation discount compounded with the regular one wrote the EFFECTIVE percent.
      const expectedDiscount = applied.effectiveDiscount ?? applied.discount ?? Number(snapshot.discount ?? 0);
      return {
        unitPrice: Math.abs(currentPrice - expectedPrice) > 1e-9 ? currentPrice : Number(snapshot.unitPrice),
        discount: !FormulaRuntimeService.sameDiscountPercent(currentDiscount, expectedDiscount) ? currentDiscount : Number(snapshot.discount ?? 0),
      };
    }
    const evaluatedBefore = !!line?.formulaResults && Object.keys(line.formulaResults).length > 0;
    if (evaluatedBefore && sourceItem && sourceItem !== line) {
      const catalogPrice = this.contextService.resolvePriceTiers(sourceItem).pricesA;
      const catalogDiscount = sourceItem?.item_prices?.[0]?.discount;
      return {
        unitPrice: Number.isFinite(catalogPrice) && catalogPrice > 0 ? catalogPrice : currentPrice,
        discount: catalogDiscount !== undefined ? FormulaContextService.parseDiscount(catalogDiscount) : currentDiscount,
      };
    }
    return { unitPrice: currentPrice, discount: currentDiscount };
  }

  /** The line keys the formula context reads money from, set to the base values. */
  static baseMoneyKeys(base: { unitPrice: number; discount: number } | null | undefined): Record<string, any> {
    if (!base) {
      return {};
    }
    return {
      perItemPrice: base.unitPrice,
      Price: base.unitPrice,
      newAddedPrice: base.unitPrice,
      discount: base.discount,
      Discount: base.discount,
    };
  }

  /** A copy of the line with its base money in place (for code that builds a formula context itself). */
  static withFormulaBase(line: any): any {
    return line?.formulaBase ? { ...line, ...FormulaRuntimeService.baseMoneyKeys(line.formulaBase) } : line;
  }

  /** Recompute formulas for an already-mapped quote line after quantity changes. */
  recalculateLine(line: any, formulaFields?: any[], impact?: QuoteImpactContext | null): any {
    if (!line) return line;
    const cloned = this.cloneQuoteLine(line);
    return this.applyToLine(
      cloned,
      line?.formulaSource || line?.fulldata?.formulaSource || line?.fulldata || cloned,
      cloned?.Quantity ?? cloned?.QTY ?? cloned?.QUANTITY ?? cloned?.quantity,
      formulaFields,
      impact,
    );
  }

  async recalculateLineAsync(line: any, impact?: QuoteImpactContext | null): Promise<any> {
    await this.ensureLoaded();
    return this.recalculateLine(line, undefined, impact);
  }

  /**
   * Write the displayed formula value into Unit Price or Discount % when Quote impact is on.
   * Uses the value shown on the line (calculated or overridden).
   */
  applyQuoteImpactToLine(line: any, impact?: QuoteImpactContext | null): any {
    if (!line) {
      return line;
    }
    const next = {
      ...line,
      item_prices: this.clonePriceRows(line.item_prices),
      itemsCustomFields: Array.isArray(line.itemsCustomFields)
        ? [...line.itemsCustomFields]
        : line.itemsCustomFields,
    };
    if (impact?.quoteStatus) {
      next.quote_status = impact.quoteStatus;
    }
    if (impact?.isEditable === false) {
      next.isEditable = false;
    }
    if (impact?.isEditable === true) {
      next.isEditable = true;
    }
    const entries = Array.isArray(next.itemsCustomFields) ? next.itemsCustomFields : [];
    return this.applyQuoteImpact(next, entries, impact, entries);
  }

  /** Group formula results across quote lines, with optional quote-level rollup. */
  summarizeQuoteFields(items: any[]): QuoteFormulaSummary[] {
    const byField = new Map<string, QuoteFormulaSummary>();

    for (const item of items || []) {
      const results = item?.formulaResults;
      if (!results || typeof results !== 'object') {
        continue;
      }

      const itemName = this.resolveLineItemName(item);
      const quantity = FormulaContextService.parseNumeric(
        item?.Quantity ?? item?.QTY ?? item?.QUANTITY ?? item?.quantity ?? 1,
      );

      for (const [key, res] of Object.entries(results)) {
        const entry = res as any;
        if (!entry?.field) {
          continue;
        }
        const fieldId = String(entry.field.id || key);
        // An errored formula contributes no number (its 0 is not a result) and carries its error.
        const error = entry.error ? String(entry.error) : undefined;
        const value =
          !error && typeof entry.value === 'number' && Number.isFinite(entry.value) ? entry.value : null;
        if (!byField.has(fieldId)) {
          // The field's CURRENT unit wins over the copy saved on the line (a unit edited after the
          // line was priced), and a percentage is never summed: 50% on two products is not 100%.
          const current = this.getFormulaFields().find((f: any) => String(f?.id) === fieldId);
          const field =
            current && current.unit_of_measure != null
              ? { ...entry.field, unit_of_measure: current.unit_of_measure }
              : entry.field;
          const rollup = rollupModeFromField(entry.field);
          const isPercent = String(field?.unit_of_measure ?? '').trim() === '%';
          byField.set(fieldId, {
            field,
            rollup: isPercent && rollup === 'sum' ? 'none' : rollup,
            total: null,
            lines: [],
          });
        }
        byField.get(fieldId)!.lines.push(error ? { itemName, quantity, value, error } : { itemName, quantity, value });
      }
    }

    for (const summary of byField.values()) {
      const numeric = summary.lines.filter((line) => line.value !== null);
      if (summary.rollup === 'sum') {
        summary.total = numeric.reduce((acc, line) => acc + (line.value as number), 0);
      } else if (summary.rollup === 'weighted_average') {
        let weighted = 0;
        let qtySum = 0;
        for (const line of numeric) {
          const qty = line.quantity > 0 ? line.quantity : 0;
          if (!qty) {
            continue;
          }
          weighted += (line.value as number) * qty;
          qtySum += qty;
        }
        summary.total = qtySum > 0 ? weighted / qtySum : null;
      } else {
        summary.total = null;
      }
    }

    return Array.from(byField.values());
  }

  getCachedFieldSums(items: any[]): QuoteFormulaSummary[] {
    const key = this.buildFieldSumsCacheKey(items);
    if (this.fieldSumsCache && this.fieldSumsCacheKey === key) {
      return this.fieldSumsCache;
    }
    this.fieldSumsCacheKey = key;
    this.fieldSumsCache = this.summarizeQuoteFields(items);
    return this.fieldSumsCache;
  }

  invalidateFieldSumsCache(): void {
    this.fieldSumsCache = null;
    this.fieldSumsCacheKey = '';
  }

  /** One loader for quote, eQuote, and item-template screens. */
  async loadFormulaFields(): Promise<void> {
    try {
      await this.ensureLoaded();
    } catch (err) {
      this.logger.log('Formula fields failed to load', err?.message || 'load failed');
    }
  }

  lineItemsFrom(source: any): any[] {
    if (Array.isArray(source)) {
      return source;
    }
    return Array.isArray(source?.data) ? source.data : [];
  }

  /** Invalidate and rebuild quote-level formula sums for the given lines. */
  rebuildFieldSums(items: any[]): QuoteFormulaSummary[] {
    const rows = Array.isArray(items) ? items : [];
    this.invalidateFieldSumsCache();
    return this.getCachedFieldSums(rows);
  }

  quoteImpact(quoteStatus: string, isEditable: boolean): QuoteImpactContext {
    return {
      quoteStatus: String(quoteStatus || '').toLowerCase(),
      isEditable,
    };
  }

  formulaResultsFingerprint(item: any): string {
    const results = item?.formulaResults;
    if (!results || typeof results !== 'object') {
      return '';
    }
    return Object.keys(results)
      .sort()
      // The unit is part of it: a unit-only edit (USD -> %) used to count as "unchanged", so a reopened
      // quote kept showing the old unit (Rod's QA, 2026-10-09).
      .map((id) => `${id}:${results[id]?.value ?? results[id]?.result ?? ''}:${results[id]?.isOverridden ? 1 : 0}:${results[id]?.field?.unit_of_measure ?? ''}`)
      .join('|');
  }

  private overrideFingerprint(item: any): string {
    return (item?.itemsCustomFields || [])
      .filter((cf: any) => cf?.isOverridden)
      .map((cf: any) => `${cf?.customerCustomFieldId}:1`)
      .join('|');
  }

  private buildFieldSumsCacheKey(items: any[]): string {
    return `${(items || []).length}:${(items || [])
      .map((item) =>
        `${item?.id ?? item?.Item_code ?? ''}|${Math.round((FormulaContextService.parseNumeric(item?.Price) || 0) * 100)}|${item?.Quantity ?? ''}|${this.formulaResultsFingerprint(item)}|${this.overrideFingerprint(item)}|${item?.quote_status ?? ''}|${item?.isEditable ?? ''}`,
      )
      .join(',')}`;
  }

  private clonePriceRows(prices: any): any {
    if (!Array.isArray(prices)) {
      return prices;
    }
    return prices.map((price: any) => ({
      ...price,
      taxes: Array.isArray(price?.taxes) ? price.taxes.map((tax: any) => ({ ...tax })) : price?.taxes,
      item_prices_taxs: Array.isArray(price?.item_prices_taxs)
        ? price.item_prices_taxs.map((tax: any) => ({ ...tax }))
        : price?.item_prices_taxs,
    }));
  }

  private quoteLineAllowsImpact(result: any): boolean {
    if (result?.isEditable === false) {
      return false;
    }
    return !isLockedQuoteStatus(result?.quote_status ?? result?.quoteStatus ?? result?.status);
  }

  /** Impact flags come from the applyToLine() argument only — no service field. */
  private applyQuoteImpact(
    result: any,
    formulaEntries: any[],
    impact?: QuoteImpactContext | null,
    rows: any[] = [],
  ): any {
    const next = this.cloneQuoteLine(result);
    if (!next) {
      return result;
    }
    if (impact?.quoteStatus) {
      next.quote_status = impact.quoteStatus;
    }
    if (impact?.isEditable === false) {
      next.isEditable = false;
    }
    if (impact?.isEditable === true) {
      next.isEditable = true;
    }
    const entries = Array.isArray(formulaEntries) ? formulaEntries : [];
    const hadCalcDiscount =
      next.formulaImpactApplied?.discount !== undefined && next.formulaImpactApplied?.discount !== null;
    if (!entries.length && !hadCalcDiscount) {
      return next;
    }
    // A user edit lifts only the SENT lock; the context's own "not editable" (void, accepted, won,
    // declined) always wins, whatever a stale isEditable on the line says.
    const allowed = impact?.userEdit
      ? impact.isEditable !== false && next.isEditable !== false
      : this.quoteLineAllowsImpact(next);
    if (!allowed) {
      if (!environment.production) {
        this.logger.log('formula:applyImpact skipped', {
          status: next.quote_status,
          editable: next.isEditable,
        });
      }
      return next;
    }

    const ranked = entries
      .filter((entry) => entry?.apply_formula !== false)
      .map((entry) => ({
        entry,
        impact: this.impactFromFormulaEntry(entry),
        order: Number(
          entry?.itemsCustomField?.execution_order ?? entry?.execution_order ?? 0,
        ),
      }))
      .filter((row) => {
        const entry = row.entry;
        if (entry?.error || entry?.calculatedValue === null) {
          return false;
        }
        return row.impact === 'unit_price' || row.impact === 'discount_percent';
      })
      .sort((a, b) => a.order - b.order);

    // The line goes back to its regular discount only when the calculation POSITIVELY no longer
    // discounts it (switched off for the product, or evaluated with no discount impact, e.g. an IF
    // now false). A missing row, a missing definition or an error is "unknown": the line keeps it.
    const revertCalcDiscount = hadCalcDiscount && this.calcDiscountKnownOff(next.formulaImpactApplied, entries, rows);
    if (!ranked.length && !revertCalcDiscount) {
      return next;
    }

    const taxRates = this.lineTaxRates(next); // read BEFORE the impact rewrites the unit price

    const lastByTarget = new Map<'unit_price' | 'discount_percent', (typeof ranked)[0]>();
    for (const row of ranked) {
      if (row.impact !== 'unit_price' && row.impact !== 'discount_percent') {
        continue;
      }
      lastByTarget.set(row.impact, row);
    }

    let wrote = false;
    // What the formula wrote, so the next run can tell its own output from a user's edit.
    const applied: { unitPrice?: number; discount?: number; effectiveDiscount?: number; discountFieldId?: string } = { ...(next.formulaImpactApplied || {}) };
    const unitPriceRow = lastByTarget.get('unit_price');
    if (unitPriceRow) {
      const value = this.parseImpactNumber(unitPriceRow.entry);
      if (value !== null && Number.isFinite(value) && value > 0) {
        next.perItemPrice = value;
        next.Price = value;
        applied.unitPrice = value;
        wrote = true;
      }
    }

    const discountRow = lastByTarget.get('discount_percent');
    if (discountRow) {
      const value = this.parseImpactNumber(discountRow.entry);
      if (value !== null && Number.isFinite(value)) {
        const percent = clampDiscountPercent(value);
        // Rod, 2026-10-09 (option A): the calculation's discount COMPOUNDS with the regular one the
        // user picked; it used to replace it (only one of the two ever applied). applyDiscount is
        // the effective figure every total, the PDF and the email read (via perItemDisc /
        // priceAfterDiscount below); regularDiscount keeps the user's part so the dialog can show it
        // and a later recalc never compounds the calculation onto its own output.
        const regular = FormulaRuntimeService.regularDiscountOf(next);
        const effective = compoundDiscountPercent(regular, percent);
        const label = `${effective}%`;
        next.discount = label;
        next.Discount = label;
        next.regularDiscount = regular;
        applied.discount = percent;
        applied.effectiveDiscount = effective;
        applied.discountFieldId = String(discountRow.entry?.customerCustomFieldId ?? discountRow.entry?.itemsCustomField?.id ?? '') || undefined;
        next.applyDiscount = effective > 0 ? effective : 0;
        // item_prices[0].discount stays the catalog's OFFERED list: the applied figure lives on
        // applyDiscount / discount / Discount only.
        wrote = true;
      }
    } else if (revertCalcDiscount) {
      const regular = FormulaRuntimeService.regularDiscountOf(next);
      const label = `${regular}%`;
      next.discount = label;
      next.Discount = label;
      next.regularDiscount = regular;
      next.applyDiscount = regular > 0 ? regular : 0;
      delete applied.discount;
      delete applied.effectiveDiscount;
      delete applied.discountFieldId;
      wrote = true;
    }

    if (wrote) {
      next.formulaImpactApplied = applied;
      if (!environment.production) {
        this.logger.log('formula:applyImpact', {
          fieldId:
            unitPriceRow?.entry?.customerCustomFieldId ??
            discountRow?.entry?.customerCustomFieldId,
          impact: unitPriceRow ? 'unit_price' : 'discount_percent',
        });
      }
      return this.recomputeLineMoney(next, taxRates);
    }
    return next;
  }

  /**
   * True only when every calculation that could have written the line's discount is KNOWN not to
   * any more: its row is explicitly switched off for the product, or it evaluated cleanly with no
   * discount impact. Which calculation wrote it is recorded (discountFieldId); a line written before
   * that was recorded considers every row whose own recipe can discount.
   */
  private calcDiscountKnownOff(applied: any, entries: any[], rows: any[]): boolean {
    const isFormula = (r: FieldTypeRow) => isFormulaType(r?.field_type_id ?? r?.itemsCustomField?.field_type_id);
    const formulaRows = (Array.isArray(rows) ? rows : []).filter(isFormula);
    const recipeOf = (r: any) => parseFormulaJson(r?.formula_json) || parseFormulaJson(r?.itemsCustomField?.formula_json);
    const candidates: string[] = applied?.discountFieldId
      ? [String(applied.discountFieldId)]
      : formulaRows
          .filter((r) => derivedQuoteImpactFromFormula(recipeOf(r)) === 'discount_percent')
          .map((r) => FormulaRuntimeService.fieldRowKey(r))
          .filter(Boolean);
    if (!candidates.length) {
      return false;
    }
    return candidates.every((id) => {
      const entry = (entries || []).find(
        (e) => String(e?.customerCustomFieldId ?? e?.itemsCustomField?.id ?? '') === id && e?.apply_formula !== false,
      );
      if (entry) {
        return !entry.error && entry.calculatedValue !== null && this.impactFromFormulaEntry(entry) !== 'discount_percent';
      }
      const row = formulaRows.find((r) => FormulaRuntimeService.fieldRowKey(r) === id);
      return !!row && row.apply_formula !== undefined && row.apply_formula !== null && !row.apply_formula;
    });
  }

  private impactFromFormulaEntry(entry: any) {
    if (entry?.quote_impact !== undefined && entry?.quote_impact !== null && entry?.quote_impact !== '') {
      return normalizeQuoteImpact(entry.quote_impact);
    }
    return quoteImpactFromField(entry?.itemsCustomField || entry);
  }

  private parseImpactNumber(entry: any): number | null {
    if (!entry || entry.isOverridden !== true) {
      const calculated = entry?.calculatedDisplayValue ?? entry?.displayValue;
      if (calculated === 'Error' || entry?.calculatedValue === null) {
        return null;
      }
    }
    const raw = entry?.value ?? entry?.displayValue;
    if (raw === undefined || raw === null || raw === '' || raw === 'Error') {
      return null;
    }
    const n = FormulaContextService.parseNumeric(raw);
    return Number.isFinite(n) ? n : null;
  }

  /** Always clone first so live table rows are never mutated in place. */
  /**
   * Line money after a formula changed the unit price or discount, with develop's quote-line model
   * (quote-item-table / create-quote mappers) so addTotals sums the same numbers it always did:
   *   perItemDisc = unit × discount%        total_discount = perItemDisc × qty
   *   perItemTax  = unit × FIRST tax rate   tax_price      = perItemTax × qty
   *   totalTaxItem = unit × SUM of tax rates (addTotals adds this per line, not × qty)
   *   Total = unit × qty                    priceAftertax  = Total + tax_price
   * Tax is on the pre-discount unit price, as develop does. Always clones: live rows are never mutated.
   */
  private recomputeLineMoney(line: any, rates: { first: number; total: number } = { first: 0, total: 0 }): any {
    const next = this.cloneQuoteLine(line);
    const quantity = FormulaContextService.parseNumeric(
      next?.Quantity ?? next?.QTY ?? next?.QUANTITY ?? next?.quantity ?? 1,
    );
    const unitPrice = FormulaContextService.parseNumeric(next?.perItemPrice ?? next?.Price);
    // Only an APPLIED discount reduces the line: applyDiscount, set by the line popup or by a
    // discount_percent formula (applyQuoteImpact, above). `discount` is the catalog's OFFERED list
    // (",10", "5,10"): reading it put an unpicked 10% on the line total while the quote totals
    // ignored it, and parseNumeric read "5,10" as 510 -> 100% (Rod's QA, 2026-10-09).
    // Always a percent ("1%" is one percent, never a 0-1 fraction); clamped to 0-100.
    const appliedRaw = next?.applyDiscount;
    const discountPercent =
      appliedRaw === undefined || appliedRaw === null || appliedRaw === '' || appliedRaw === 0
        ? 0
        : clampDiscountPercent(FormulaContextService.parseDiscount(appliedRaw));
    const perItemDisc = unitPrice * (discountPercent / 100);
    const total = unitPrice * quantity;
    const totalDiscount = perItemDisc * quantity;
    const perItemTax = unitPrice * rates.first;
    const taxPrice = perItemTax * quantity;

    next.perItemPrice = unitPrice;
    next.Price = unitPrice;
    next.perItemDisc = perItemDisc;
    next.Total = total;
    next.total_discount = totalDiscount;
    next.priceAfterDiscount = total - totalDiscount;
    next.discPrice = (unitPrice - perItemDisc).toFixed(2);
    next.perItemTax = perItemTax;
    next.totalTaxItem = unitPrice * rates.total;
    next.tax_price = taxPrice;
    next.priceAftertax = total + taxPrice;
    return next;
  }

  /**
   * The line's tax rates. Preferred source: the rates the mapper already applied
   * (perItemTax / totalTaxItem over the CURRENT unit price), which carries any tax override chosen
   * when the item was added. Fallback: the line's `taxes` list (first rate, and the sum).
   */
  private lineTaxRates(line: any): { first: number; total: number } {
    // The line's own tax list (what the mapper applied) is the source of truth: deriving the rate
    // from the previous output drifted whenever that output was stale.
    if (Array.isArray(line?.taxes)) {
      const rates = line.taxes.map((tax: any) =>
        FormulaContextService.parseNumeric(Array.isArray(tax?.value) ? tax.value[0] : tax?.value) / 100,
      );
      return { first: rates[0] || 0, total: rates.reduce((sum: number, r: number) => sum + r, 0) };
    }
    // Lines without a tax list (none built by develop's mappers): the ratios they carry.
    const prevUnit = FormulaContextService.parseNumeric(line?.perItemPrice ?? line?.Price);
    if (prevUnit > 0) {
      return {
        first: FormulaContextService.parseNumeric(line?.perItemTax) / prevUnit,
        total: FormulaContextService.parseNumeric(line?.totalTaxItem ?? line?.perItemTax) / prevUnit,
      };
    }
    return { first: 0, total: 0 };
  }

  private resolveLineItemName(item: any): string {
    return (
      item?.itemName ||
      item?.Item_name ||
      item?.['Item name'] ||
      item?.name ||
      item?.fulldata?.Item_name ||
      item?.fulldata?.name ||
      // No name: the panels show a translated "Product" (COMMON.Formula unnamed product).
      ''
    );
  }

  private collectCustomFieldRows(lineItem: any, result: any): any[] {
    const candidates = [
      result?.itemsCustomFields,
      result?.items_custom_field_datas,
      lineItem?.itemsCustomFields,
      lineItem?.items_custom_field_datas,
    ];
    const merged = new Map<string, any>();
    const unkeyed: any[] = [];
    for (const list of candidates) {
      if (!Array.isArray(list)) {
        continue;
      }
      for (const row of list) {
        const id = String(
          row?.customerCustomFieldId ?? row?.custom_field_id ?? row?.itemsCustomField?.id ?? '',
        );
        const name = String(row?.itemsCustomField?.name ?? '');
        const key = id ? `id:${id}` : name ? `name:${name}` : '';
        if (!key) {
          unkeyed.push(row);
          continue;
        }
        const existing = merged.get(key);
        if (!existing) {
          merged.set(key, row);
          continue;
        }
        // Candidates run line-first (the quote line's own rows), catalog last. The line's state
        // (apply_formula, value, override, recipe) wins; the catalog only fills what the line lacks.
        merged.set(key, {
          ...FormulaRuntimeService.fillMissing(existing, row),
          itemsCustomField: FormulaRuntimeService.fillMissing(
            existing?.itemsCustomField || {},
            row?.itemsCustomField || {},
          ),
        });
      }
    }
    return [...merged.values(), ...unkeyed];
  }

  /** `primary` with any null/undefined key filled from `fallback`. */
  private static fillMissing(primary: any, fallback: any): any {
    const out: any = { ...(fallback || {}) };
    for (const [k, v] of Object.entries(primary || {})) {
      if (v !== undefined && v !== null) {
        out[k] = v;
      }
    }
    return out;
  }

  private collectApplyFlags(datas: any[]): Map<string, boolean> {
    const applyFlags = new Map<string, boolean>();
    for (const d of datas) {
      const fieldId = String(
        d.customerCustomFieldId || d.custom_field_id || d.itemsCustomField?.id || '',
      );
      if (!fieldId) {
        continue;
      }
      if (d.apply_formula !== undefined && d.apply_formula !== null) {
        applyFlags.set(fieldId, !!d.apply_formula);
      }
    }
    return applyFlags;
  }
}
